import React from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  Stack,
  SimpleGrid,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaLock, FaChartLine, FaBrain, FaTags } from 'react-icons/fa';

const Feature = ({ title, text, icon }: { title: string; text: string; icon: any }) => {
  return (
    <Stack align="center" textAlign="center">
      <Icon as={icon} w={10} h={10} color="blue.500" />
      <Text fontWeight="bold" fontSize="lg">{title}</Text>
      <Text color={useColorModeValue('gray.600', 'gray.400')}>{text}</Text>
    </Stack>
  );
};

const LandingPage = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Container maxW="container.xl" py={20}>
        <Stack spacing={8} align="center" textAlign="center">
          <Heading
            as="h1"
            size="2xl"
            bgGradient="linear(to-r, blue.400, purple.500)"
            bgClip="text"
            lineHeight="1.5"
          >
            Transform Your Journaling Experience
          </Heading>
          <Text fontSize="xl" color={useColorModeValue('gray.600', 'gray.400')} maxW="2xl">
            Discover deeper insights about yourself through AI-powered journaling. 
            Write freely, track patterns, and uncover meaningful connections in your personal journey.
          </Text>
          <Stack direction="row" spacing={4}>
            <Button
              as={RouterLink}
              to="/register"
              colorScheme="blue"
              size="lg"
              height="4rem"
              px="2rem"
            >
              Start Journaling
            </Button>
            <Button
              as={RouterLink}
              to="/pricing"
              variant="outline"
              size="lg"
              height="4rem"
              px="2rem"
            >
              View Pricing
            </Button>
          </Stack>
        </Stack>
      </Container>

      {/* Features Section */}
      <Box bg={useColorModeValue('gray.50', 'gray.900')} py={20}>
        <Container maxW="container.xl">
          <Stack spacing={12}>
            <Heading textAlign="center" size="xl">
              Features That Enhance Your Journey
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={10}>
              <Feature
                icon={FaLock}
                title="Advanced Encryption"
                text="Your journal entries are fully encrypted in our database so your private thoughts remain private."
              />
              <Feature
                icon={FaBrain}
                title="AI-Powered Insights"
                text="Ask questions about your patterns and receive personalized insights from your journal entries."
              />
              <Feature
                icon={FaChartLine}
                title="Mood Tracking"
                text="Track your emotional journey and identify patterns in your well-being over time."
              />
              <Feature
                icon={FaTags}
                title="Smart Organization"
                text="Organize entries with tags and custom fields to easily reflect on specific aspects of your life."
              />
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={20}>
        <Stack
          direction={{ base: 'column', md: 'row' }}
          spacing={8}
          align="center"
          justify="space-between"
          bg={useColorModeValue('blue.50', 'blue.900')}
          p={8}
          borderRadius="lg"
        >
          <Stack spacing={4} maxW="lg">
            <Heading size="lg">Start Your Journey of Self-Discovery</Heading>
            <Text fontSize="lg" color={useColorModeValue('gray.600', 'gray.400')}>
              Turn your daily reflections into meaningful insights with AI-powered journaling.
            </Text>
          </Stack>
          <Button
            as={RouterLink}
            to="/register"
            colorScheme="blue"
            size="lg"
            height="4rem"
            px="2rem"
          >
            Start Free
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default LandingPage; 